import { Workbox } from 'workbox-window';

export interface ServiceWorkerCallbacks {
    onUpdateAvailable?: () => void;
    onUpdateReady?: () => void;
    onOffline?: () => void;
    onOnline?: () => void;
}

let wb: Workbox | null = null;

// Custom event to notify React components about SW updates
const dispatchUpdateEvent = () => {
    window.dispatchEvent(new CustomEvent('sw-update-available'));
};

export const registerServiceWorker = (callbacks?: ServiceWorkerCallbacks) => {
    if ('serviceWorker' in navigator && import.meta.env.PROD) {
        wb = new Workbox('/sw.js', { scope: '/' });

        // Listen for waiting service worker
        wb.addEventListener('waiting', () => {
            console.log('New service worker waiting to activate');
            dispatchUpdateEvent();
            callbacks?.onUpdateAvailable?.();
        });

        // Listen for controlling service worker change
        wb.addEventListener('controlling', () => {
            console.log('Service worker is now controlling the page');
            window.location.reload();
        });

        // Listen for activated event
        wb.addEventListener('activated', (event) => {
            if (!event.isUpdate) {
                console.log('Service worker activated for the first time');
            } else {
                console.log('Service worker updated');
                callbacks?.onUpdateReady?.();
            }
        });

        // Register the service worker
        wb.register()
            .then((registration) => {
                console.log('Service worker registered successfully:', registration);

                // Check for updates every 60 seconds
                setInterval(() => {
                    registration?.update();
                }, 60000);
            })
            .catch((error) => {
                console.error('Service worker registration failed:', error);
            });

        // Listen for online/offline events
        window.addEventListener('online', () => {
            console.log('App is online');
            callbacks?.onOnline?.();
        });

        window.addEventListener('offline', () => {
            console.log('App is offline');
            callbacks?.onOffline?.();
        });
    } else if (!('serviceWorker' in navigator)) {
        console.warn('Service workers are not supported in this browser');
    } else {
        console.log('Service worker registration skipped (not in production mode)');
    }
};

export const updateServiceWorker = () => {
    if (wb) {
        wb.messageSkipWaiting();
    }
};

export const getServiceWorkerRegistration = (): Promise<ServiceWorkerRegistration | undefined> => {
    if ('serviceWorker' in navigator) {
        return navigator.serviceWorker.getRegistration();
    }

    return Promise.resolve(undefined);
};

export const unregisterServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();

        if (registration) {
            await registration.unregister();
            console.log('Service worker unregistered');
        }
    }
};

export const getWorkboxInstance = () => wb;
